import Collapse from "@mui/material/Collapse";
import AddRecipients from "@/components/campaignBuilder/recipients/AddRecipients";
import Expiration from "@/components/campaignBuilder/recipients/Expiration";
import GiftReminders from "@/components/campaignBuilder/recipients/GiftReminders";
import LinkActivation from "@/components/campaignBuilder/recipients/LinkActivation";
import RecipientNotification from "@/components/campaignBuilder/recipients/RecipientNotification";
import Schedule from "@/components/campaignBuilder/recipients/Schedule";
import { LockedSection } from "@/components/campaignBuilder/recipients/shared";
import { RECIPIENTS_LOCKED_NOTICE } from "@/constants/campaignBuilder";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import {
  expiryLock,
  isRecipientsLocked,
  sendDateLock,
  sendMethodLock,
} from "@/utils/campaign-gating-helper";
import { resolveCampaignTimeZone, zonedWallTimeToIso } from "@/utils/timezone-helper";

/** The current campaign-level send instant from the builder's date + time fields, read in the campaign's zone. */
function scheduleInstant(date: string, time: string, timeZone: string): Date | null {
  if (!date) return null;
  const iso = zonedWallTimeToIso(date, (time || "09:00").slice(0, 5), timeZone);
  return iso ? new Date(iso) : null;
}

/**
 * Step 2 — recipients plus the three campaign-level fields that stay editable AFTER scheduling.
 *
 * Each field has its OWN lock now, not one lock over the whole step (which is what the single
 * `<fieldset>` used to be). Payment freezes the recipient table and gift reminders for good, but send
 * date (while Scheduled, until T-5 min), expiration (through Active) and send method (Draft only)
 * stay editable on their own status rules — mirrored server-side by assertPostScheduleEditable.
 */
export default function RecipientsStep() {
  const {
    notificationMode,
    linkToList,
    recipientsAccordions,
    toggleRecipientsAccordion,
    campaignStatus,
    scheduleMode,
    scheduleDate,
    scheduleTime,
    scheduleTz,
    activationTz,
  } = useCampaignBuilder();
  const isGfgSend = notificationMode === "gfg";
  // The recipient list appears for gfg AND for manual links tied to a recipient list (Path B — one
  // link per person). Only Path A (manual, anonymous links by quantity) has no list. Schedule, gift
  // reminders and the per-recipient send columns stay gfg-only — Path B reuses the list, not the send.
  const manualLinked = notificationMode === "manual" && linkToList === true;
  const showRecipientList = isGfgSend || manualLinked;

  // Recipient table + gift reminders: frozen wholesale by payment (null = CREATE mode ⇒ open).
  const recipientsLocked = isRecipientsLocked(campaignStatus);
  const sendMethod = sendMethodLock(campaignStatus);
  const sendDate = sendDateLock(
    campaignStatus,
    scheduleInstant(
      scheduleDate,
      scheduleTime,
      resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz }),
    ),
    scheduleMode,
  );
  const expiry = expiryLock(campaignStatus);

  return (
    <div className="max-w-225 mx-auto px-5 py-8 w-full flex flex-col gap-4">
      {/* Page heading, above the accordions, for every send method (prototype: the "Add Recipients"
          title row is separate from the recipients box — which manual hides — and stays put). The
          Bulk Upload / Add Manually pair that used to live here now lives inside the box. */}
      <h1 className="mb-1">Add Recipients</h1>

      {/* Recipient Notification is the first section for ALL campaigns (prototype reorder): the
          send method drives everything below it, so it's chosen first. */}
      <LockedSection locked={sendMethod.locked} reason={sendMethod.reason}>
        <RecipientNotification
          isOpen={sendMethod.locked || recipientsAccordions.has("notification")}
          onToggle={() => toggleRecipientsAccordion("notification")}
        />
      </LockedSection>

      {/* Recipient list shows for gfg and for manual Path B (link to a list); hidden only for manual
          Path A, which hands out anonymous links by quantity. Only the notification mode + path drive
          this, never the lock. */}
      <Collapse in={showRecipientList} unmountOnExit>
        <LockedSection
          locked={recipientsLocked}
          reason={recipientsLocked ? RECIPIENTS_LOCKED_NOTICE : undefined}
        >
          <AddRecipients />
        </LockedSection>
      </Collapse>

      {/* Schedule (GfG send) and Link Activation (manual) share this slot — exactly one shows. */}
      <Collapse in={isGfgSend} unmountOnExit>
        <LockedSection locked={sendDate.locked} reason={sendDate.reason}>
          <Schedule
            isOpen={sendDate.locked || recipientsAccordions.has("schedule")}
            onToggle={() => toggleRecipientsAccordion("schedule")}
          />
        </LockedSection>
      </Collapse>
      <Collapse in={!isGfgSend} unmountOnExit>
        <LinkActivation
          isOpen={recipientsAccordions.has("activation")}
          onToggle={() => toggleRecipientsAccordion("activation")}
        />
      </Collapse>

      <LockedSection locked={expiry.locked} reason={expiry.reason}>
        <Expiration
          isOpen={expiry.locked || recipientsAccordions.has("expiry")}
          onToggle={() => toggleRecipientsAccordion("expiry")}
        />
      </LockedSection>

      {/* Gift Reminders hidden in manual mode; frozen by payment like the recipient list. Its own
          open state is Pro-gated, so the lock asks for open via forceOpen rather than driving isOpen. */}
      <Collapse in={isGfgSend} unmountOnExit>
        <LockedSection locked={recipientsLocked}>
          <GiftReminders forceOpen={recipientsLocked} />
        </LockedSection>
      </Collapse>
    </div>
  );
}
